import { MutationTree } from 'vuex';
import { ProuniState } from './state';
import {
  ProuniApplication,
  ProuniDocument,
  ProuniDocumentType,
  ProuniEligibility,
  ProuniFamilyMember,
} from './types';

const mutations: MutationTree<ProuniState> = {
  SET_PROUNI_APPLICATION(state, application: ProuniApplication | null) {
    state.application = application;
  },
  SET_PROUNI_ELIGIBILITY(state, eligibility: ProuniEligibility | null) {
    state.eligibility = eligibility;
  },
  // Keep the in-store documents in sync after an upload so navigating back to a
  // step shows the already-uploaded files (matched by type + family member).
  UPSERT_PROUNI_DOCUMENT(state, document: ProuniDocument) {
    if (!state.application) return;

    const documents = state.application.documents || [];
    // Free-form ("other") documents are always appended — several may share the
    // same type, so they must never collapse into one another. Every other type
    // is matched by type + family member: a fresh upload replaces the previous
    // file of that slot.
    const index =
      document.documentType === ProuniDocumentType.other
        ? -1
        : documents.findIndex(
            current =>
              current.documentType === document.documentType &&
              (current.familyMemberId || null) === (document.familyMemberId || null),
          );

    const nextDocuments =
      index >= 0
        ? documents.map((current, i) => (i === index ? document : current))
        : [...documents, document];

    state.application = { ...state.application, documents: nextDocuments };
  },
  REMOVE_PROUNI_DOCUMENT(state, id: number) {
    if (!state.application) return;

    const documents = (state.application.documents || []).filter(current => current.id !== id);
    state.application = { ...state.application, documents };
  },
  // Keep family members in sync so navigating back to the step shows them.
  UPSERT_PROUNI_FAMILY_MEMBER(state, member: ProuniFamilyMember) {
    if (!state.application) return;

    const members = state.application.familyMembers || [];
    const index = members.findIndex(current => current.id === member.id);

    const nextMembers =
      index >= 0
        ? members.map((current, i) => (i === index ? { ...current, ...member } : current))
        : [...members, member];

    state.application = { ...state.application, familyMembers: nextMembers };
  },
  REMOVE_PROUNI_FAMILY_MEMBER(state, id: number) {
    if (!state.application) return;

    const members = (state.application.familyMembers || []).filter(current => current.id !== id);
    // Drop that member's documents too, so no orphan upload state lingers.
    const documents = (state.application.documents || []).filter(
      document => document.familyMemberId !== id,
    );

    state.application = { ...state.application, familyMembers: members, documents };
  },
};

export default mutations;
